import React, { memo, useCallback, useState } from 'react'
import { DomesticCartBusItem } from '~/api/converters/domestic/cart/ProductSection/Bus/index.type'
import BusOption from '~/app/components-nds/domestic/cart/cart-product-section/items/bus/option'
import { ProductBadge, Divider, Dialog } from '@yanolja-rnd/nol-design-system'
import { isNative } from '~/app/utils/typeguards'
import { getYaNative } from '@yanolja-org/ya-native'
import { CartPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'
import { zIndexMax } from '~/app/components/styles/units'

/**
 * 버스 아이템 컴포넌트의 Props 타입
 */
interface BusItemProps {
  /** 버스 아이템 데이터 */
  item: DomesticCartBusItem
}

/**
 * 버스 아이템 컴포넌트
 * 장바구니의 버스 상품을 표시하는 메인 컴포넌트
 */
const BusItem: React.FC<BusItemProps> = ({ item }) => {
  const { itemTitle, options } = item
  const [alertOpen, setAlertOpen] = useState(false)

  const onTitleClick = useCallback(
    async (index?: number) => {
      const {
        clientLogs: {
          click: { kinesis },
        },
      } = item

      kinesis && (await CartPageLog.ProductIdLinkCartOption.click({ productOption: kinesis }))

      const yaNative = getYaNative()
      if (isNative(yaNative)) {
        yaNative.deepLink({ link: options[index || 0].deepLinkUrl })
      } else {
        setAlertOpen(true)
      }
    },
    [item, options]
  )

  return (
    <section className="space-y-16" role="region" aria-label="버스 상품 목록">
      <article className="rounded-16 bg-static-white p-16 pf-pc:p-24">
        {/* 그룹 헤더 */}
        <header className="pb-16">
          {/* 버스 Badge */}
          <ProductBadge
            type="filled_secondary"
            size="medium"
            className="mb-6 px-6 py-2"
            aria-label="교통수단 카테고리"
          >
            교통
          </ProductBadge>

          {/* 타이틀 */}
          <div
            className="cursor-pointer typography-subtitle-18-bold"
            onClick={() => onTitleClick()}
          >
            {itemTitle}
          </div>
        </header>

        <Divider size="small" type="horizontal" className="mb-20" />

        {/* 옵션 목록 */}
        <div
          className="flex flex-col gap-40"
          role="list"
          aria-label={`${itemTitle} 버스 옵션 목록`}
        >
          {options.map((option, index) => {
            return (
              <BusOption key={index} option={option} onTitleClick={() => onTitleClick(index)} />
            )
          })}
        </div>
        <Dialog
          id="bus-alert-dialog"
          show={alertOpen}
          title={
            <span>
              고속버스 상품은
              <br />
              앱에서만 확인할 수 있어요
            </span>
          }
          positiveButton={{ text: '확인', onClick: () => setAlertOpen(false) }}
          zIndex={zIndexMax}
        />
      </article>
    </section>
  )
}

// React.memo를 사용하여 props가 변경되지 않으면 리렌더링 방지
export default memo(BusItem)
